import { useMemo, useState } from 'react';
import { TbSparkles } from 'react-icons/tb';

import { InsightTheme } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { titleCase } from '@/utils';
import { cn } from '@/utils/cn';
import { NewEventSheet } from 'views/calendar/widgets/NewEventSheet';

import { DashboardCard } from '../DashboardCard';
import { useActiveInsights } from './insight-queries';
import { InsightItem } from './InsightItem';
import { InsightSettings } from './InsightSettings';
import { useInsightSettings } from './useInsightSettings';

const CounterBadge = ({ count, isActive }: { count: number; isActive: boolean }) => (
    <span
        className={cn(
            'flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs leading-none font-medium tabular-nums',
            isActive ? 'text-cocoa-1000 bg-white' : 'bg-cocoa-0 text-cocoa-500',
        )}
    >
        {count}
    </span>
);

export const ProactiveInsightsList = () => {
    const [activeTheme, setActiveTheme] = useState<InsightTheme | null>(null);
    const [scheduleSheet, setScheduleSheet] = useState<{
        open: boolean;
        defaultHousehold?: { value: string; label: string };
    }>({ open: false });
    const { data: insights = [], isLoading, isError } = useActiveInsights();
    const { tagOrder, setTagOrder, hiddenTags, toggleTagVisibility, resetSettings } = useInsightSettings();

    const handleSchedule = (defaultHousehold?: { value: string; label: string }) =>
        setScheduleSheet({ open: true, defaultHousehold });

    const themes = useMemo(
        () => Object.values(InsightTheme).filter((theme) => insights.some((i) => i.theme === theme)),
        [insights],
    );

    const filteredInsights = useMemo(() => {
        const visible = insights.filter(
            (i) => !hiddenTags.includes(i.tag) && (activeTheme === null || i.theme === activeTheme),
        );
        // Tags not present in tagOrder (e.g. a newly introduced insight type) sort to the
        // end rather than jumping to the front via indexOf's -1.
        const rank = (tag: (typeof tagOrder)[number]) => {
            const index = tagOrder.indexOf(tag);
            return index === -1 ? Number.MAX_SAFE_INTEGER : index;
        };
        return [...visible].sort((a, b) => rank(a.tag) - rank(b.tag));
    }, [insights, activeTheme, hiddenTags, tagOrder]);

    return (
        <DashboardCard
            size="full"
            className="flex flex-col border-0 p-0 py-6"
            title="Proactive Insights"
            titleRight={
                <div className="flex items-center gap-2">
                    {themes.map((theme) => (
                        <Button
                            key={theme}
                            variant={activeTheme === theme ? 'highlighted' : 'ghost2'}
                            size="sm"
                            onClick={() => setActiveTheme(activeTheme === theme ? null : theme)}
                            className="gap-1.5 text-base"
                        >
                            <CounterBadge
                                count={insights.filter((i) => !hiddenTags.includes(i.tag) && i.theme === theme).length}
                                isActive={activeTheme === theme}
                            />
                            {titleCase(theme)}
                        </Button>
                    ))}
                    <InsightSettings
                        tagOrder={tagOrder}
                        hiddenTags={hiddenTags}
                        onTagOrderChange={setTagOrder}
                        onToggleTag={toggleTagVisibility}
                        onReset={resetSettings}
                    />
                </div>
            }
        >
            <div className="max-h-[400px] w-full overflow-y-auto">
                {isLoading ? (
                    <LoadingState />
                ) : isError ? (
                    <ErrorState />
                ) : filteredInsights.length > 0 ? (
                    <div>
                        {filteredInsights.map((insight) => (
                            <InsightItem key={insight.insightGUID} insight={insight} onSchedule={handleSchedule} />
                        ))}
                    </div>
                ) : (
                    <EmptyState theme={activeTheme} />
                )}
            </div>
            <NewEventSheet
                key={scheduleSheet.defaultHousehold?.value ?? 'no-household'}
                open={scheduleSheet.open}
                onOpenChange={(open) => setScheduleSheet(() => ({ open }))}
                defaultHousehold={scheduleSheet.defaultHousehold}
                disableHouseholdSelection={!!scheduleSheet.defaultHousehold}
            />
        </DashboardCard>
    );
};

const LoadingState = () => (
    <div>
        {[1, 2, 3].map((i) => (
            <div key={i} className="border-border flex animate-pulse items-center gap-6 border-b py-4">
                <div className="h-10 w-10 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/2 rounded bg-gray-200" />
                    <div className="h-3 w-1/3 rounded bg-gray-200" />
                </div>
            </div>
        ))}
    </div>
);

const ErrorState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <TbSparkles className="mb-2 h-8 w-8 text-gray-400" />
        <p className="text-sm font-medium">Unable to load insights</p>
        <p className="text-xs">Please try refreshing the page</p>
    </div>
);

const EmptyState = ({ theme }: { theme: InsightTheme | null }) => (
    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <TbSparkles className="mb-2 h-8 w-8 text-gray-400" />
        <p className="text-sm font-medium">{theme === null ? 'No insights' : `No ${titleCase(theme)} insights`}</p>
        <p className="text-xs">Check back later for new opportunities</p>
    </div>
);
